/** Pagination controls — extracted from TableParts.tsx for the 400-line cap. */
import { useContext, type ReactNode } from "react";
import { CaretDoubleLeftIcon } from "@phosphor-icons/react/CaretDoubleLeft";
import { CaretLeftIcon } from "@phosphor-icons/react/CaretLeft";
import { CaretRightIcon } from "@phosphor-icons/react/CaretRight";
import { CaretDoubleRightIcon } from "@phosphor-icons/react/CaretDoubleRight";
import { Icon } from "../../Icons/Icon";
import { PageEdgeContext } from "../../PageLayout/Internal/PageEdgeContext";

export function PaginationButton_({
  onClick,
  disabled,
  children,
  testId,
}: {
  onClick: () => void;
  disabled: boolean;
  children: ReactNode;
  testId?: string | undefined;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      {...(testId !== undefined && { "data-testid": testId })}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-content-faint transition-colors hover:bg-muted hover:text-content disabled:pointer-events-none disabled:opacity-30"
    >
      {children}
    </button>
  );
}

export function PaginationBar_({
  visible,
  insidePanel,
  totalRows,
  selectedCount,
  currentPage,
  totalPages,
  currentPageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  footerLeft,
  footerRight,
  testIdPrefix,
}: {
  visible: boolean;
  insidePanel: boolean;
  totalRows: number;
  selectedCount: number;
  currentPage: number;
  totalPages: number;
  currentPageSize: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  footerLeft?: ReactNode | undefined;
  footerRight?: ReactNode | undefined;
  /**
   * Prefix for the footer's selector hooks, from the table's `tableId`. Only the
   * caller-supplied id is used, never the one Table derives from column keys —
   * a testid built out of a column list would be unusable and would churn every
   * time a column moved. Yields `<prefix>-pagination-{summary,page-indicator,
   * first,prev,next,last}`.
   */
  testIdPrefix?: string | undefined;
}) {
  const testId = (part: string) =>
    testIdPrefix === undefined
      ? undefined
      : `${testIdPrefix}-pagination-${part}`;
  const atPageEdge = useContext(PageEdgeContext);
  // Nothing to page through and nothing to say about it — an empty table is
  // clearer without a footer reading "0 total rows" under it. A caller that put
  // its own content in the footer still gets it.
  if (!visible) return undefined;
  if (totalRows === 0 && !footerLeft && !footerRight && selectedCount === 0) {
    return undefined;
  }

  const showPageControls = totalPages > 1;

  // A controlled <select> shows its first option when value matches none, so a
  // page size outside pageSizeOptions would silently display the wrong number.
  // Always include the active size so the dropdown reflects what the table uses.
  const sizeOptions = pageSizeOptions.includes(currentPageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, currentPageSize].sort((a, b) => a - b);

  return (
    <div
      className={
        insidePanel
          ? `flex items-center justify-between border-t border-muted-foreground ${atPageEdge ? "px-6" : "px-4"} py-3`
          : "flex items-center justify-between px-1 pt-3"
      }
    >
      {(() => {
        // A paginated table says which slice you are looking at; an
        // unpaginated one has no slice to name, so it keeps the plain count.
        const firstRow = currentPage * currentPageSize + 1;
        const lastRow = Math.min(
          totalRows,
          (currentPage + 1) * currentPageSize,
        );
        const summary = showPageControls
          ? `Showing ${firstRow}–${lastRow} of ${totalRows}`
          : `${totalRows} total row${totalRows !== 1 ? "s" : ""}`;
        const left = (
          <span
            className="text-xs font-medium text-content-muted"
            {...(testId("summary") !== undefined && {
              "data-testid": testId("summary"),
            })}
          >
            {selectedCount > 0 && `${selectedCount} selected · `}
            {footerLeft ?? summary}
          </span>
        );
        const right = footerRight ? (
          <span className="text-xs font-bold text-content-secondary">
            {footerRight}
          </span>
        ) : undefined;
        return showPageControls ? (
          <div className="flex items-center gap-4">
            {left}
            {right}
          </div>
        ) : (
          <>
            {left}
            {right}
          </>
        );
      })()}
      {showPageControls && (
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-content">
              Rows per page
            </span>
            <select
              value={currentPageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-7 rounded-md border-2 border-border bg-card px-1.5 text-xs font-medium focus:outline-none"
            >
              {sizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <span
            className="text-xs font-medium text-content-muted"
            {...(testId("page-indicator") !== undefined && {
              "data-testid": testId("page-indicator"),
            })}
          >
            Page {currentPage + 1} of {totalPages}
          </span>
          <div className="flex items-center gap-0.5">
            <PaginationButton_
              onClick={() => onPageChange(0)}
              testId={testId("first")}
              disabled={currentPage === 0}
            >
              <Icon icon={CaretDoubleLeftIcon} size="sm" />
            </PaginationButton_>
            <PaginationButton_
              onClick={() => onPageChange(Math.max(0, currentPage - 1))}
              testId={testId("prev")}
              disabled={currentPage === 0}
            >
              <Icon icon={CaretLeftIcon} size="sm" />
            </PaginationButton_>
            <PaginationButton_
              onClick={() =>
                onPageChange(Math.min(totalPages - 1, currentPage + 1))
              }
              testId={testId("next")}
              disabled={currentPage >= totalPages - 1}
            >
              <Icon icon={CaretRightIcon} size="sm" />
            </PaginationButton_>
            <PaginationButton_
              onClick={() => onPageChange(totalPages - 1)}
              testId={testId("last")}
              disabled={currentPage >= totalPages - 1}
            >
              <Icon icon={CaretDoubleRightIcon} size="sm" />
            </PaginationButton_>
          </div>
        </div>
      )}
    </div>
  );
}
